import { SOVEREIGN_JEWEL_ID } from '@kenocyber/shared';

/**
 * A aparência de cada joia: paleta própria, família de movimento (V28C).
 *
 * ## Porque isto vive na web e não no catálogo
 *
 * Uma família e uma paleta são APRESENTAÇÃO. O catálogo diz o que uma joia é -
 * o seu modo de socket, a sua forma medida, o seu nome - e não deve ganhar
 * campos sobre cores e animações, que mudam com a direção de arte e não com o
 * jogo.
 *
 * ## A cor é DA JOIA; a família é só o movimento
 *
 * Esta é a regra que a segunda volta visual trouxe, e ela inverte o que estava
 * aqui antes. A família já não fornece cor nenhuma: fornece forma, ritmo e
 * comportamento do acento. A cor de cada peça vem da PRÓPRIA peça, declarada
 * uma a uma - um Núcleo Atômico grafite não pode pedir emprestado o laranja da
 * Chama da Destruição só por partilharem família, e um Passo Alado dourado não
 * pode acender-se de azul porque a sua família se chama vento.
 *
 * As paletas foram lidas da arte, e não inventadas. Onde a arte é escura, a
 * energia é que dá a luz: preto sozinho não desenha um contorno legível, por
 * isso o Núcleo Atômico contorna-se a laranja sobre grafite e o Passo Alado a
 * dourado sobre preto.
 *
 * ## As famílias comunicam FUNÇÃO, não raridade
 *
 * Duas joias da mesma família movem-se de forma parecida porque fazem coisas
 * parecidas. Não há hierarquia entre as catorze e nada nesta tabela a insinua:
 * nenhuma tem mais camadas, mais brilho ou um acabamento "melhor". A única que
 * se distingue é a soberana, e distingue-se por ser outra coisa - não por ser
 * superior numa escala.
 *
 * ## A cor nunca é a única diferença
 *
 * Cada família tem um MOVIMENTO próprio - arcos ramificados, fluxo circular,
 * brasa, batida dupla, varredura firme, fluxo irregular, respiração, reflexo
 * direcional - e a soberana segue a silhueta em vez de um aro. Quem não
 * distingue cores continua a ter forma e cadência para ler.
 */

export const JEWEL_FAMILIES = [
  'electric',
  'plasma',
  'ember',
  'blood',
  'shield',
  'toxic',
  'vital',
  'gale',
  'sovereign',
] as const;
export type JewelFamily = (typeof JEWEL_FAMILIES)[number];

/** As três cores de uma joia. Nenhuma peça tem mais camadas do que outra. */
export interface JewelPalette {
  /** A cor que desenha o contorno. É a que se lê primeiro, e à distância. */
  readonly primary: string;
  /** O contraste do acento: a segunda cor da arte, nunca uma variação da primeira. */
  readonly secondary: string;
  /** O núcleo quente, quase branco, que dá a sensação de luz. */
  readonly core: string;
}

export interface JewelFamilyBehaviour {
  readonly family: JewelFamily;
  /** O nome da família, para quem lê o código e para o `data-family` do DOM. */
  readonly label: string;
  /** O que o acento FAZ, em palavras. O CSS implementa-o por família. */
  readonly motion: string;
  /** Duração do ciclo do acento, em milissegundos. */
  readonly idleMs: number;
}

/** O que uma carta precisa de saber para se desenhar. */
export interface JewelLook extends JewelFamilyBehaviour {
  readonly palette: JewelPalette;
}

/**
 * As nove famílias, e o que cada uma faz.
 *
 * Os tempos são diferentes entre si e primos o suficiente para nunca voltarem a
 * alinhar-se de forma visível: quinze cartas a pulsar em uníssono lêem-se como
 * um alarme. Os das famílias rápidas são curtos de propósito - um arco elétrico
 * lento não é um arco elétrico.
 */
export const JEWEL_FAMILY_BEHAVIOURS: Readonly<Record<JewelFamily, JewelFamilyBehaviour>> =
  Object.freeze({
    electric: Object.freeze({
      family: 'electric',
      label: 'Elétrica',
      motion: 'arcos curtos e ramificações rápidas',
      idleMs: 2300,
    }),
    plasma: Object.freeze({
      family: 'plasma',
      label: 'Plasma',
      motion: 'núcleo quente e fluxo circular',
      idleMs: 4100,
    }),
    /*
     * O fogo separou-se do plasma.
     *
     * Estavam juntos, e não fazem a mesma coisa: um núcleo nuclear tem um
     * fluxo circular contido, e uma chama tem brasa a subir. Duas assinaturas
     * internas diferentes precisam de duas famílias - a alternativa era uma
     * família com duas animações, que é uma família a fingir.
     */
    ember: Object.freeze({
      family: 'ember',
      label: 'Fogo',
      motion: 'brasa vermelha e laranja com centro quase branco',
      idleMs: 3300,
    }),
    blood: Object.freeze({
      family: 'blood',
      label: 'Sangue',
      motion: 'dois pulsos, uma pausa e uma onda carmesim',
      idleMs: 3700,
    }),
    shield: Object.freeze({
      family: 'shield',
      label: 'Escudo',
      motion: 'varredura firme e geométrica',
      idleMs: 5300,
    }),
    toxic: Object.freeze({
      family: 'toxic',
      label: 'Tóxica',
      motion: 'fluxo lento e irregular, sem cadência fixa',
      idleMs: 6100,
    }),
    vital: Object.freeze({
      family: 'vital',
      label: 'Vital',
      motion: 'respiração luminosa com foco interno',
      idleMs: 4700,
    }),
    gale: Object.freeze({
      family: 'gale',
      label: 'Vento',
      motion: 'reflexo direcional, sempre no mesmo sentido',
      idleMs: 2900,
    }),
    sovereign: Object.freeze({
      family: 'sovereign',
      label: 'Soberana',
      motion: 'pulso duplo lento, com surto ocasional, ao longo da silhueta',
      idleMs: 3100,
    }),
  });

/**
 * A que família cada joia pertence.
 *
 * Escrito por extenso, uma linha por joia, em vez de inferido do nome do
 * ficheiro. Um `includes('damage')` poria a redução de dano na família do dano,
 * que é exatamente o contrário do que ela faz.
 */
const FAMILY_BY_ID: Readonly<Record<string, JewelFamily>> = Object.freeze({
  jewel_saw: 'electric',
  jewel_blade_cut: 'electric',
  jewel_attack_speed: 'electric',
  jewel_missile: 'ember',
  jewel_damage: 'ember',
  jewel_atomic_bomb: 'plasma',
  jewel_lifesteal: 'blood',
  jewel_bleeding: 'blood',
  jewel_damage_reflection: 'shield',
  jewel_damage_reduction: 'shield',
  jewel_poison: 'toxic',
  jewel_max_health: 'vital',
  jewel_regeneration: 'vital',
  jewel_move_speed: 'gale',
  [SOVEREIGN_JEWEL_ID]: 'sovereign',
});

/**
 * A paleta de cada joia, uma a uma.
 *
 * Lidas da arte. Onde duas peças se aproximam - e há três vermelhos aqui - a
 * diferença fica no DESENHO e no RITMO do acento, e não numa cor torcida para
 * se distinguir: um Orbe Vampírico que fosse cor-de-rosa para não se parecer
 * com a Garra Hemorrágica deixaria de pertencer à sua própria esfera.
 */
const PALETTE_BY_ID: Readonly<Record<string, JewelPalette>> = Object.freeze({
  /* Branco frio e prata; os segmentos escuros são os intervalos do acento. */
  jewel_saw: Object.freeze({ primary: '#e8f2ff', secondary: '#94a9bf', core: '#ffffff' }),
  /* Laranja e âmbar, núcleo quase branco. */
  jewel_missile: Object.freeze({ primary: '#ff8a1f', secondary: '#ffc247', core: '#fff4e0' }),
  /* Vermelho-sangue e carmesim. */
  jewel_lifesteal: Object.freeze({ primary: '#b3121f', secondary: '#e0294f', core: '#ffd9df' }),
  /* Vermelho escuro e aço. */
  jewel_damage_reflection: Object.freeze({
    primary: '#8e1a1a',
    secondary: '#b8c2cc',
    core: '#ffe3e3',
  }),
  /* Azul profundo e azul elétrico. */
  jewel_blade_cut: Object.freeze({ primary: '#1636b0', secondary: '#3fa9ff', core: '#e6f4ff' }),
  /*
   * Grafite com energia laranja.
   *
   * A cor primária é a ENERGIA e não o casco: preto sozinho não produz luz, e
   * um contorno preto sobre uma carta escura é um contorno que não existe.
   */
  jewel_atomic_bomb: Object.freeze({ primary: '#ff7a18', secondary: '#3a3f47', core: '#ffd9a8' }),
  /* Preto e vermelho. */
  jewel_bleeding: Object.freeze({ primary: '#e01030', secondary: '#1a1116', core: '#ffd4dc' }),
  /* Roxo e lilás. */
  jewel_poison: Object.freeze({ primary: '#8b2fd6', secondary: '#c79bff', core: '#f3e6ff' }),
  /* Verde com acentos dourados. */
  jewel_max_health: Object.freeze({ primary: '#2fbf6b', secondary: '#ffcf3d', core: '#eaffef' }),
  /* Vermelho e laranja, núcleo branco. */
  jewel_damage: Object.freeze({ primary: '#ff2d1f', secondary: '#ff9d1f', core: '#ffffff' }),
  /* Esmeralda e ciano claro. */
  jewel_damage_reduction: Object.freeze({
    primary: '#0fbf7a',
    secondary: '#68e8ff',
    core: '#e6fffb',
  }),
  /* Preto com dourado luminoso - o dourado é que se vê. */
  jewel_move_speed: Object.freeze({ primary: '#ffc93d', secondary: '#15171d', core: '#fff3cc' }),
  /* Verde com pulso vermelho. */
  jewel_regeneration: Object.freeze({ primary: '#35d17a', secondary: '#ff3b5c', core: '#e9ffef' }),
  /* Amarelo e dourado. */
  jewel_attack_speed: Object.freeze({ primary: '#ffd21f', secondary: '#f2a007', core: '#fff6d0' }),
  /* Rubi e carmesim, núcleo branco-avermelhado. */
  [SOVEREIGN_JEWEL_ID]: Object.freeze({
    primary: '#e0143c',
    secondary: '#a30b28',
    core: '#ffe3e6',
  }),
});

export function jewelFamily(definitionId: string): JewelFamily {
  /*
   * A elétrica como recurso, e não como omissão silenciosa.
   *
   * Uma joia sem família seria uma carta sem borda; devolver algo desenhável é
   * melhor do que devolver nada. Há uma prova a exigir que as quinze estejam
   * nomeadas, por isso este recurso não se usa hoje - e no dia em que a décima
   * sexta chegar, é a prova que falha, não a carta.
   */
  return FAMILY_BY_ID[definitionId] ?? 'electric';
}

/** A paleta declarada da joia, com um cinzento neutro como último recurso. */
export function jewelPalette(definitionId: string): JewelPalette {
  return (
    PALETTE_BY_ID[definitionId] ?? { primary: '#9fb3c8', secondary: '#5c7183', core: '#f2f7ff' }
  );
}

export function jewelLook(definitionId: string): JewelLook {
  return {
    ...JEWEL_FAMILY_BEHAVIOURS[jewelFamily(definitionId)],
    palette: jewelPalette(definitionId),
  };
}
